import type { AssistanceLevel, Difficulty, MatchFormat, NewGameDraft } from './types.js';
import { STANDARD_RULESET_ID, TOURNAMENT_RULESET_ID } from './standard-ruleset.js';

export interface NewGamePreset {
  id: string;
  label: string;
  description: string;
  draft: NewGameDraft;
}

export const DEFAULT_NEW_GAME_DRAFT: NewGameDraft = {
  difficulty: 'competitive',
  assistance: 'off',
  dealer: 'random',
  matchFormat: 'seven-marks',
  animationSpeed: 'normal',
  rulesetId: STANDARD_RULESET_ID,
  confirmBeforePlay: false,
};

export const NEW_GAME_PRESETS: NewGamePreset[] = [
  {
    id: 'casual-relaxed',
    label: 'Casual & Relaxed',
    description: 'One practice hand with relaxed opponents and helpful hints.',
    draft: {
      difficulty: 'casual',
      assistance: 'legal-only',
      dealer: 'random',
      matchFormat: 'practice-hand',
      animationSpeed: 'normal',
      rulesetId: STANDARD_RULESET_ID,
      confirmBeforePlay: false,
    },
  },
  {
    id: 'balanced-challenge',
    label: 'Balanced Challenge',
    description: 'Play to seven marks against competitive opponents.',
    draft: {
      difficulty: 'competitive',
      assistance: 'off',
      dealer: 'random',
      matchFormat: 'seven-marks',
      animationSpeed: 'normal',
      rulesetId: STANDARD_RULESET_ID,
      confirmBeforePlay: false,
    },
  },
  {
    id: 'expert-match',
    label: 'Expert Match',
    description: 'Expert opponents and tournament rules, without special contracts.',
    draft: {
      difficulty: 'expert',
      assistance: 'off',
      dealer: 'random',
      matchFormat: 'seven-marks',
      animationSpeed: 'fast',
      rulesetId: TOURNAMENT_RULESET_ID,
      confirmBeforePlay: true,
    },
  },
  {
    id: 'grandad-challenge',
    label: 'Grandad Challenge',
    description: 'Play to seven marks against opponents that learn your habits.',
    draft: {
      difficulty: 'grandad',
      assistance: 'off',
      dealer: 'random',
      matchFormat: 'seven-marks',
      animationSpeed: 'normal',
      rulesetId: STANDARD_RULESET_ID,
      confirmBeforePlay: true,
    },
  },
];

export const DIFFICULTY_OPTIONS: Array<{ value: Difficulty; label: string }> = [
  { value: 'casual', label: 'Casual' },
  { value: 'competitive', label: 'Competitive' },
  { value: 'expert', label: 'Expert' },
  { value: 'grandad', label: 'Grandad' },
];

export const ASSISTANCE_OPTIONS: Array<{ value: AssistanceLevel; label: string }> = [
  { value: 'off', label: 'Off' },
  { value: 'legal-only', label: 'Legal-move emphasis' },
  { value: 'suggested-move', label: 'Suggested move' },
];

export const MATCH_FORMAT_OPTIONS: Array<{ value: MatchFormat; label: string; description: string }> = [
  {
    value: 'practice-hand',
    label: 'Practice Hand',
    description: 'Play one hand only.',
  },
  {
    value: 'seven-marks',
    label: 'Standard Match',
    description: 'First team to seven marks wins.',
  },
];

export const DEALER_OPTIONS: Array<{ value: NewGameDraft['dealer']; label: string }> = [
  { value: 'random', label: 'Random' },
  { value: 0, label: 'South (you)' },
  { value: 2, label: 'North (partner)' },
  { value: 1, label: 'East' },
  { value: 3, label: 'West' },
];

export const ANIMATION_SPEED_OPTIONS: Array<{ value: NewGameDraft['animationSpeed']; label: string }> = [
  { value: 'slow', label: 'Slow' },
  { value: 'normal', label: 'Normal' },
  { value: 'fast', label: 'Fast' },
  { value: 'reduced', label: 'Reduced motion' },
];

export function matchFormatLabel(format: MatchFormat): string {
  if (format === 'best-of-three') {
    return 'Standard Match';
  }
  return MATCH_FORMAT_OPTIONS.find((option) => option.value === format)?.label ?? format;
}

export function normalizeMatchFormat(format: MatchFormat): MatchFormat {
  return format === 'best-of-three' ? 'seven-marks' : format;
}

export function normalizeDraft(draft: NewGameDraft): NewGameDraft {
  const matchFormat = normalizeMatchFormat(draft.matchFormat);
  return matchFormat === draft.matchFormat ? draft : { ...draft, matchFormat };
}

export function assistanceLabel(level: AssistanceLevel): string {
  return ASSISTANCE_OPTIONS.find((option) => option.value === level)?.label ?? level;
}

export function cloneDraft(draft: NewGameDraft): NewGameDraft {
  return { ...draft };
}

export function applyDifficultyPreselect(
  draft: NewGameDraft,
  difficulty: Difficulty | null,
): NewGameDraft {
  if (!difficulty) {
    return draft;
  }
  return { ...draft, difficulty };
}
